/**
 * Office register of incident and near-miss reports (#/incidents, #/incidents/<id>): open or closed,
 * the prompts and when each was done, photos, office notes, and the date the insurer was told, with a
 * reminder and an overdue flag from the client layer's insurer rule. Reports are never deleted.
 */
import { useMemo, useState } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../../components/States";
import { BRAND } from "../../lib/config";
import { addDays, fmtDate, fmtPlainDate, localYmd } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { hashParams, useHash } from "../../lib/route";
import type { Incident, IncidentRow, Injury } from "../../lib/supabase";
import { useAsync } from "../../lib/useAsync";
import { getIncident, incidentPhotoBlob, listIncidents, reclassificationLink, reclassifyNearMiss, updateIncident } from "./field-data";
import { INJURY_LABEL, IncidentReport, Prompts, incidentChip } from "./IncidentReport";

type InsurerState = "n/a" | "done" | "ok" | "reminder" | "overdue";

/** Same rule as the API's reminder job: days counted in local calendar days from the report date. */
export function insurerState(i: Pick<IncidentRow, "kind" | "reported_at" | "insurer_notified_on" | "insurer_clock_from">, today = localYmd()): { state: InsurerState; due: string | null } {
  const ins = BRAND.field?.incidents.insurer;
  if (!ins || !ins.appliesTo.includes(i.kind)) return { state: "n/a", due: null };
  // A reclassified near miss: the clock runs from the near miss's report time.
  const reported = localYmd(new Date(i.insurer_clock_from ?? i.reported_at));
  const due = addDays(reported, ins.dueDays);
  if (i.insurer_notified_on) return { state: "done", due };
  if (today > due) return { state: "overdue", due };
  return { state: today >= addDays(reported, ins.reminderDays) ? "reminder" : "ok", due };
}

function InsurerCell({ row }: { row: IncidentRow }) {
  const { state, due } = insurerState(row);
  if (state === "n/a") return <span className="muted">Not needed</span>;
  if (state === "done") return <span>Told {fmtPlainDate(row.insurer_notified_on!)}</span>;
  if (state === "overdue") return <span className="expiry"><Chip tone="primary">Overdue</Chip><span className="sub sub--primary">Was due {fmtPlainDate(due!)}</span></span>;
  return <span className="expiry">{state === "reminder" ? <Chip tone="accent">Due soon</Chip> : null}<span className="sub">Tell by {fmtPlainDate(due!)}</span></span>;
}

export function Incidents() {
  const [id] = hashParams(useHash());
  if (id) return <IncidentView key={id} id={id} />;
  return <IncidentList />;
}

function IncidentList() {
  const { data, error, loading, reload } = useAsync(listIncidents);
  const [q, setQ] = useState("");
  const [show, setShow] = useState("open");
  const [reporting, setReporting] = useState(false);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((r) => {
      if (show === "open" && r.status !== "open") return false;
      if (show === "closed" && r.status !== "closed") return false;
      if (show === "insurer" && !["reminder", "overdue"].includes(insurerState(r).state)) return false;
      return !needle || [r.reference, r.description, r.reporter_name, r.jobs?.reference, r.jobs?.title, r.category_label].some((s) => s?.toLowerCase().includes(needle));
    });
  }, [data, q, show]);
  const insurerDue = (data ?? []).filter((r) => ["reminder", "overdue"].includes(insurerState(r).state)).length;

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Operations"
        title="Incidents"
        meta={`Incident and near-miss reports from the field and the office. Reports are kept as records and cannot be deleted.${BRAND.field?.incidents.insurer ? ` The insurer must be told within ${BRAND.field.incidents.insurer.dueDays} days.` : ""}`}
        actions={<button type="button" className="btn btn--primary" onClick={() => setReporting((r) => !r)}>{reporting ? "Close" : <><Icon name="plus" size={18} /> New report</>}</button>}
      />
      {reporting && (
        <div className="card"><div className="card__head"><h2 className="card__title">New report</h2></div><div className="card__body">
          <IncidentReport from={{ office: {} }} onClose={() => { setReporting(false); reload(); }} onSent={() => reload()} />
        </div></div>
      )}
      <FilterBar count={data ? `Showing ${plural(rows.length, "report")}` : undefined}>
        <SearchField label="Search reports" placeholder="Search reference, job, reporter or text" value={q} onChange={setQ} />
        <SelectField label="Show" value={show} onChange={setShow} options={[
          { value: "open", label: "Open" },
          ...(BRAND.field?.incidents.insurer ? [{ value: "insurer", label: `Insurer to be told${data ? ` (${insurerDue})` : ""}` }] : []),
          { value: "closed", label: "Closed" },
          { value: "all", label: "All" },
        ]} />
      </FilterBar>
      {loading && !data && <Skeleton columns={6} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && rows.length === 0 && <EmptyState icon="check" title={data.length ? "No matching reports" : "No reports yet"}>{data.length ? "Try a different search or filter." : "Reports sent from job packs appear here."}</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack">
            <thead><tr><th scope="col">Report</th><th scope="col">What</th><th scope="col">When</th><th scope="col">Job</th><th scope="col">Status</th><th scope="col">Insurer</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td data-label="Report"><a className="strong" href={`#/incidents/${r.id}`}>{r.reference}</a><span className="sub">{r.reporter_name}</span></td>
                  <td data-label="What">
                    {incidentChip(r.kind, r.notifiable)}
                    <span className="sub">{r.reclassified_from ? "Reclassified from a near miss" : r.category_label ?? r.description.slice(0, 80)}</span>
                  </td>
                  <td data-label="When" className="nowrap">{fmtDate(r.occurred_at)}</td>
                  <td data-label="Job">{r.jobs ? `${r.jobs.reference} ${r.jobs.title}` : <span className="muted">None</span>}</td>
                  <td data-label="Status"><Chip value={r.status} /></td>
                  <td data-label="Insurer"><InsurerCell row={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const toIncident = (r: IncidentRow): Incident => ({
  id: r.id, reference: r.reference, kind: r.kind, bookingId: r.booking_id, jobId: r.job_id, occurredAt: r.occurred_at, location: r.location,
  description: r.description, peopleInvolved: r.people_involved, injury: r.injury, immediateAction: r.immediate_action, category: r.category,
  categoryLabel: r.category_label, categoryOther: r.category_other, notifiable: r.notifiable, prompts: r.prompts, reporterName: r.reporter_name,
  reportedAt: r.reported_at, status: r.status, closedAt: r.closed_at, officeNotes: r.office_notes, insurerNotifiedOn: r.insurer_notified_on,
  officeAlertSentAt: r.office_alert_sent_at, reclassifiedFrom: r.reclassified_from ?? null, reclassifyReason: r.reclassify_reason ?? null,
  insurerClockFrom: r.insurer_clock_from ?? null,
});

function IncidentView({ id }: { id: string }) {
  const { data, error, loading, reload } = useAsync(async () => {
    const row = await getIncident(id);
    return { row, link: row ? await reclassificationLink(row) : null };
  }, [id]);
  const r = data?.row ?? null;
  const link = data?.link ?? null;
  const [reclassifying, setReclassifying] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);
  const [insurer, setInsurer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();

  if (loading && !data) return <section className="page"><Skeleton rows={4} columns={2} /></section>;
  if (error) return <section className="page"><ErrorState message={error} onRetry={reload} /></section>;
  if (!r) return <section className="page"><EmptyState icon="search" title="Report not found"><a href="#/incidents">Back to incidents</a></EmptyState></section>;
  const ins = insurerState(r);

  async function save(patch: Parameters<typeof updateIncident>[1], done: string) {
    setBusy(true);
    setNotice(null);
    try {
      await updateIncident(r!.id, patch);
      ok(done);
      setNotes(null);
      setInsurer(null);
      reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  const none = <span className="muted">Not given</span>;
  return (
    <section className="page" aria-labelledby="page-title" style={{ maxWidth: 900 }}>
      <a className="backlink" href="#/incidents"><Icon name="arrow" size={16} /> All incidents</a>
      <PageHeader
        eyebrow={r.reclassified_from
          ? `${r.reference} · First reported ${fmtDate(r.insurer_clock_from ?? r.reported_at)} by ${r.reporter_name} · Reclassified ${fmtDate(r.reported_at)}`
          : `${r.reference} · Reported ${fmtDate(r.reported_at)} by ${r.reporter_name}`}
        title={r.kind === "near_miss" ? `Near miss: ${r.category_label ?? "Other"}${r.category_other ? ` (${r.category_other})` : ""}` : "Incident"}
        actions={
          <button type="button" className="btn btn--outline" disabled={busy} onClick={() => void save({ status: r.status === "open" ? "closed" : "open" }, r.status === "open" ? "Report closed." : "Report reopened.")}>
            {r.status === "open" ? "Close the report" : "Reopen"}
          </button>
        }
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {r.kind === "near_miss" && link && (
        <p className="notice notice--ok" role="status"><Icon name="check" size={18} /><span>Reclassified as incident <a href={`#/incidents/${link.id}`}>{link.reference}</a>. This near miss is kept as reported.</span></p>
      )}
      {r.reclassified_from && (
        <p className="notice notice--ok" role="status">
          <Icon name="warning" size={18} />
          <span>Reclassified from near miss {link ? <a href={`#/incidents/${link.id}`}>{link.reference}</a> : "report"}: {(r.reclassify_reason ?? "").replace(/[.\s]+$/, "")}. The insurer clock runs from the near miss report, {fmtDate(r.insurer_clock_from ?? r.reported_at)}.</span>
        </p>
      )}
      {r.kind === "near_miss" && !link && (
        reclassifying
          ? <Reclassify id={r.id} onCancel={() => setReclassifying(false)} />
          : <div><button type="button" className="btn btn--outline btn--sm" onClick={() => setReclassifying(true)}>Reclassify as an incident</button></div>
      )}
      <div className="card">
        <div className="card__head"><h2 className="card__title">What was reported</h2>{incidentChip(r.kind, r.notifiable)}</div>
        <div className="card__body">
          <dl className="facts">
            <div className="fact"><dt>When</dt><dd>{fmtDate(r.occurred_at)}</dd></div>
            <div className="fact"><dt>Where on site</dt><dd>{r.location ?? none}</dd></div>
            <div className="fact"><dt>Job</dt><dd>{r.jobs && r.job_id ? <a href={`#/jobs/${r.job_id}`}>{r.jobs.reference} {r.jobs.title}</a> : none}</dd></div>
            <div className="fact"><dt>Injuries</dt><dd>{INJURY_LABEL[r.injury]}</dd></div>
            <div className="fact fact--wide"><dt>What happened</dt><dd className="prewrap">{r.description}</dd></div>
            <div className="fact fact--wide"><dt>People involved</dt><dd>{r.people_involved ?? none}</dd></div>
            <div className="fact fact--wide"><dt>Action taken straight away</dt><dd className="prewrap">{r.immediate_action ?? none}</dd></div>
            <div className="fact"><dt>Status</dt><dd><Chip value={r.status} />{r.closed_at && <span className="sub">Closed {fmtDate(r.closed_at)}</span>}</dd></div>
            <div className="fact"><dt>Office emailed</dt><dd>{r.office_alert_sent_at ? fmtDate(r.office_alert_sent_at) : <span className="muted">No (email not set up or it failed)</span>}</dd></div>
          </dl>
        </div>
      </div>

      {r.prompts.length > 0 && (
        <div className="card"><div className="card__body"><Prompts as="office" incident={toIncident(r)} /></div></div>
      )}

      <div className="card">
        <div className="card__head"><h2 className="card__title">Photos</h2></div>
        <div className="card__body">
          {r.reclassified_from && link && <p className="small muted">Photos sent with the near miss are on <a href={`#/incidents/${link.id}`}>{link.reference}</a>.</p>}
          {(r.incident_photos ?? []).length === 0 ? <p className="muted">No photos.</p> : (
            <div className="packactions">
              {r.incident_photos!.map((p, i) => (
                <button key={p.id} type="button" className="btn btn--outline btn--sm" onClick={() => void openBlobInNewTab(() => incidentPhotoBlob(r.id, p.id)).catch(fail)}>
                  <Icon name="file" size={16} /> Photo {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card__head"><h2 className="card__title">Office</h2></div>
        <div className="card__body stack">
          {ins.state !== "n/a" && (
            <div className="stack" style={{ gap: 8 }}>
              <span className="field__label">Insurer told on</span>
              <span>{r.insurer_notified_on ? fmtPlainDate(r.insurer_notified_on) : <InsurerCell row={r} />}</span>
              <div className="addcrew">
                <input className="input" type="date" aria-label="Date the insurer was told" max={localYmd()} value={insurer ?? r.insurer_notified_on ?? ""} onChange={(e) => setInsurer(e.target.value)} />
                <button type="button" className="btn btn--ink btn--sm" disabled={busy || !insurer} onClick={() => void save({ insurerNotifiedOn: insurer }, "Insurer date saved.")}>Save date</button>
              </div>
            </div>
          )}
          <label className="field">
            <span className="field__label">Office notes</span>
            <textarea className="input" rows={4} value={notes ?? r.office_notes ?? ""} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <div className="form-actions">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy || notes === null} onClick={() => void save({ officeNotes: notes?.trim() || null }, "Notes saved.")}>Save notes</button>
          </div>
        </div>
      </div>
    </section>
  );
}

/** A near miss that turned out to cause damage or injury becomes a linked incident; the near miss stays as reported. */
function Reclassify({ id, onCancel }: { id: string; onCancel: () => void }) {
  const [reason, setReason] = useState("");
  const [injury, setInjury] = useState<Injury>("none");
  const [notifiable, setNotifiable] = useState(false);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail } = useNotice();
  async function go() {
    setBusy(true);
    try {
      const incident = await reclassifyNearMiss(id, { reason: reason.trim(), injury, notifiable });
      window.location.hash = `#/incidents/${incident.id}`;
    } catch (e) {
      fail(e);
      setBusy(false);
    }
  }
  return (
    <div className="card card--alert" role="dialog" aria-labelledby={`rc-${id}`}>
      <div className="card__head"><h2 className="card__title" id={`rc-${id}`}>Reclassify as an incident</h2></div>
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <p>A new incident report is made from this near miss and linked to it. The near miss stays exactly as reported and is closed. The insurer clock runs from when the near miss was first reported.</p>
        <label className="field">
          <span className="field__label">Why: what damage or injury was found</span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The car damaged the ute mirror" />
        </label>
        <label className="field">
          <span className="field__label">Injuries</span>
          <select className="input" value={injury} onChange={(e) => setInjury(e.target.value as Injury)}>{(Object.keys(INJURY_LABEL) as Injury[]).map((k) => <option key={k} value={k}>{INJURY_LABEL[k]}</option>)}</select>
        </label>
        <label className="check"><input type="checkbox" checked={notifiable} onChange={(e) => setNotifiable(e.target.checked)} /> {BRAND.field?.incidents.notifiableQuestion ?? "Notifiable incident"}</label>
        <div className="form-actions">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy || reason.trim().length < 5} aria-busy={busy} onClick={() => void go()}>{busy ? "Saving" : "Reclassify"}</button>
          <button type="button" className="btn btn--outline btn--sm" disabled={busy} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
